/**
 * Records the route a shopper actually walked through the navigation, so a
 * product page can breadcrumb through the branch they used rather than a
 * guess.
 *
 * Why this exists: product cards link to the bare /products/{handle} URL, so
 * Shopify's `collection` global is empty on the product page. The Liquid
 * breadcrumb therefore has to guess, and it picks the deepest menu node
 * matching any collection the product belongs to. For a product that sits in
 * several collections, or a collection that appears in more than one menu
 * branch (Christmas Cards lives under both "Cards" and "Holidays > Holiday
 * Cards"), that guess is frequently not the route the shopper took.
 *
 * Two pieces of state, both in sessionStorage so they expire with the tab:
 *
 *   mh:menu-click — the last navigation link clicked, as {url, title, depth}.
 *     Captured from the data attributes the mega menu already stamps on every
 *     link, so no changes to its render path were needed.
 *   mh:menu-path  — the resolved trail for the last collection the shopper
 *     viewed, as {url, trail: [{title, url}, ...]}.
 *
 * Everything here is progressive enhancement. With no JS, no storage, or no
 * recorded path, the server-rendered breadcrumb stands as it is.
 */

const CLICK_KEY = 'mh:menu-click';
const PATH_KEY = 'mh:menu-path';
const MAX_DEPTH = 5;

/** Selector covering every menu surface a shopper can navigate from. */
const MENU_LINK_SELECTOR = [
  'a[data-mh-mm-track="menu-link"]', // mega menu, desktop bar + panels + mobile drawer
  'a.mh-sidenav__link', // collection sub-nav
  'a.mh-sidenav__parent-link',
].join(',');

/**
 * Normalizes a URL to a path-only form, matching snippets/menu-tree-url-path.liquid.
 * @param {string} url
 * @returns {string}
 */
export function normalizePath(url) {
  if (!url) return '';
  let path = String(url);
  if (path.includes('://')) {
    const tail = path.split('://')[1];
    const host = tail.split('/')[0];
    path = path.replace(`https://${host}`, '').replace(`http://${host}`, '');
  }
  return path.split('?')[0].split('#')[0];
}

/**
 * @param {string} key
 * @returns {any} The stored value, or null when storage is unavailable or the
 *   entry is missing or corrupt.
 */
function read(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (storageError) {
    // Private browsing, or a malformed entry. Fall back to the SSR trail.
    return null;
  }
}

/**
 * @param {string} key
 * @param {any} value
 */
function write(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (storageError) {
    // Storage unavailable. The breadcrumb stays as rendered.
  }
}

/** Reads the menu tree without a network request where possible. */
let treePromise = null;

/**
 * Resolves the menu tree, preferring the locale-correct JSON the header
 * already inlines. The external URL is the mega menu's own source, so it is
 * an HTTP cache hit rather than a second download.
 *
 * @returns {Promise<{ items: any[] } | null>}
 */
export function loadMenuTree() {
  if (treePromise) return treePromise;

  treePromise = (async () => {
    const inline = document.querySelector('script[data-mh-mm-tree]');
    if (inline?.textContent) {
      try {
        const parsed = JSON.parse(inline.textContent);
        if (Array.isArray(parsed?.items)) return parsed;
      } catch (parseError) {
        // Fall through to the external tree.
      }
    }

    // The mega menu's own source first, so this is an HTTP cache hit. The
    // breadcrumb carries the same URL for pages rendered without the header.
    const host = document.querySelector('[data-menu-json-url]');
    const fallback = document.querySelector('mh-breadcrumb[data-tree-url]');
    const url =
      (host instanceof HTMLElement ? host.dataset.menuJsonUrl : null) ||
      (fallback instanceof HTMLElement ? fallback.dataset.treeUrl : null);
    if (!url) return null;

    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) return null;
      const data = await response.json();
      return Array.isArray(data?.items) ? data : null;
    } catch (fetchError) {
      return null;
    }
  })();

  return treePromise;
}

/**
 * Collects every trail in the tree whose final node points at `targetPath`.
 * A collection can legitimately appear in more than one branch, which is
 * exactly the ambiguity pickTrail resolves.
 *
 * @param {any[]} items
 * @param {string} targetPath
 * @returns {Array<Array<{ title: string, url: string }>>}
 */
export function collectTrails(items, targetPath) {
  /** @type {Array<Array<{ title: string, url: string }>>} */
  const trails = [];
  if (!targetPath) return trails;

  const walk = (nodes, trail, depth) => {
    if (!Array.isArray(nodes) || depth > MAX_DEPTH) return;
    for (const node of nodes) {
      const entry = { title: node.title || '', url: normalizePath(node.url || '') };
      const nodeTrail = [...trail, entry];
      if (entry.url && entry.url === targetPath) trails.push(nodeTrail);
      walk(node.children, nodeTrail, depth + 1);
    }
  };
  walk(items, [], 1);

  return trails;
}

/**
 * Picks the trail that best matches the link the shopper actually clicked.
 *
 * The clicked link's own label is rarely enough: "Christmas Cards" sits under
 * both "Cards" and "Holidays > Holiday Cards" under the exact same label. What
 * separates them is the context the link was clicked in — the mega menu column
 * it belongs to, and the top-level item whose panel was open — so those
 * ancestor titles carry the most weight here.
 *
 * Panel-relative depth is only a tiebreak: it is measured from the panel, not
 * the tree root, and the mobile drawer does not record it at all.
 *
 * Where everything ties, the first branch in menu order wins, which is the one
 * the mega menu itself lists first.
 *
 * @param {Array<Array<{ title: string, url: string }>>} trails
 * @param {{ url?: string, title?: string, depth?: number, ancestors?: string[] } | null} click
 * @returns {Array<{ title: string, url: string }> | null}
 */
export function pickTrail(trails, click) {
  if (trails.length === 0) return null;
  if (trails.length === 1 || !click) return trails[0];

  const ancestors = Array.isArray(click.ancestors) ? click.ancestors.filter(Boolean) : [];

  let best = trails[0];
  let bestScore = -1;

  for (const trail of trails) {
    const titles = trail.map((node) => node.title);
    const tail = trail[trail.length - 1];
    let score = 0;

    // The column and panel the link was clicked in must appear in the branch.
    for (const ancestor of ancestors) {
      if (titles.includes(ancestor)) score += 3;
    }

    if (click.title && tail.title === click.title) score += 2;
    // The shopper may have clicked a parent ("Holidays") and arrived here from
    // a page below it, so credit a trail that contains the clicked node too.
    if (click.url && trail.some((node) => node.url === click.url)) score += 1;
    if (click.depth && trail.length === click.depth + 1) score += 1;

    if (score > bestScore) {
      bestScore = score;
      best = trail;
    }
  }

  return best;
}

/**
 * The trail for the last collection the shopper viewed, if one was recorded.
 * @returns {{ url: string, trail: Array<{ title: string, url: string }> } | null}
 */
export function getFollowedPath() {
  const stored = read(PATH_KEY);
  if (!stored || typeof stored.url !== 'string' || !Array.isArray(stored.trail)) return null;
  if (stored.trail.length === 0) return null;
  return stored;
}

/**
 * Resolves the menu trail for a path, disambiguated by whatever link the
 * shopper last clicked.
 *
 * @param {string} path - A normalized path, e.g. /collections/christmas-cards
 * @returns {Promise<Array<{ title: string, url: string }> | null>}
 */
export async function resolveTrail(path) {
  if (!path) return null;
  const tree = await loadMenuTree();
  if (!tree) return null;
  return pickTrail(collectTrails(tree.items, path), read(CLICK_KEY));
}

/**
 * Records the menu link being clicked. Runs during the click that starts the
 * navigation, so it is kept to a single synchronous storage write.
 *
 * @param {Event} event
 */
function handleClick(event) {
  const target = /** @type {Element | null} */ (event.target);
  const link = target?.closest?.(MENU_LINK_SELECTOR);
  if (!(link instanceof HTMLAnchorElement)) return;

  const href = link.getAttribute('href');
  if (!href || href === '#') return;

  const depth = Number(link.dataset.mhMmDepth);

  write(CLICK_KEY, {
    url: normalizePath(href),
    // The mega menu stamps the node title; the sub-nav uses its link text.
    title: link.dataset.mhMmTitle || link.textContent?.trim() || '',
    depth: Number.isFinite(depth) && depth > 0 ? depth : 0,
    ancestors: collectAncestorTitles(link),
  });
}

/**
 * The titles of the menu nodes the clicked link sits under, read from the
 * surrounding DOM. This is what tells "Cards > Christmas Cards" apart from
 * "Holidays > Holiday Cards > Christmas Cards".
 *
 * @param {HTMLAnchorElement} link
 * @returns {string[]}
 */
function collectAncestorTitles(link) {
  const titles = [];

  // Desktop: the column the link belongs to.
  const groupTitle = link.closest('.mh-megamenu__group')?.querySelector('.mh-megamenu__group-title');
  if (groupTitle && groupTitle !== link) titles.push(nodeTitle(groupTitle));

  // Desktop: the top-level item whose panel is open. Panels are portaled out of
  // the bar, so the link back to the trigger is aria-controls, not the DOM tree.
  const panelId = link.closest('[data-mh-mm-panel]')?.id;
  if (panelId) {
    const trigger = document.querySelector(`[aria-controls="${CSS.escape(panelId)}"][data-mh-mm-title]`);
    if (trigger instanceof HTMLElement) titles.push(trigger.dataset.mhMmTitle ?? '');
  }

  // Mobile drawer: the heading names the level the shopper drilled into.
  const drawerTitle = link.closest('[data-mh-mm-drawer]')?.querySelector('[data-mh-mm-drawer-title]');
  if (drawerTitle) titles.push(nodeTitle(drawerTitle));

  // Collection sub-nav: the parent collection it is nested under.
  const parentLink = link.closest('.mh-sidenav')?.querySelector('.mh-sidenav__parent-link');
  if (parentLink && parentLink !== link) titles.push(nodeTitle(parentLink));

  return titles.filter(Boolean);
}

/**
 * The menu title an element stands for. Prefers the stamped attribute, and
 * otherwise reads the element's own text while skipping the decorative bits
 * that sit inside these links — the mega menu's badge span and the sub-nav's
 * back arrow are both children, so plain textContent would pick them up.
 *
 * @param {Element} el
 * @returns {string}
 */
function nodeTitle(el) {
  if (el instanceof HTMLElement && el.dataset.mhMmTitle) return el.dataset.mhMmTitle;

  const ownText = [...el.childNodes]
    .filter((node) => node.nodeType === Node.TEXT_NODE)
    .map((node) => node.textContent ?? '')
    .join(' ')
    .trim();

  return ownText || (el.textContent ?? '').trim();
}

/**
 * On a collection page, resolves which menu branch the shopper is in and
 * stores it for the product pages they open next.
 */
async function recordCollectionPath() {
  const currentPath = normalizePath(window.location.pathname);
  if (!currentPath.startsWith('/collections/')) return;

  // /collections/x/products/y is a product page, not a collection page.
  if (currentPath.includes('/products/')) return;

  const tree = await loadMenuTree();
  if (!tree) return;

  const trail = pickTrail(collectTrails(tree.items, currentPath), read(CLICK_KEY));
  if (!trail) return;

  write(PATH_KEY, { url: currentPath, trail });
}

document.addEventListener('click', handleClick, { capture: true });
recordCollectionPath();
